import OpenInNewRoundedIcon from "@mui/icons-material/OpenInNewRounded";
import SaveRoundedIcon from "@mui/icons-material/SaveRounded";
import { Stack, TableCell, TableRow, TextField } from "@mui/material";
import { useKeycloak } from "@react-keycloak/web";
import { useQueryClient } from "@tanstack/react-query";
import { useAtom, useAtomValue, WritableAtom } from "jotai";
import moment from "moment";
import {
    FC,
    SetStateAction,
    useEffect,
    useState
} from "react";
import {
    baseAtom,
    cohortAtom,
    IPeriod,
    teamAtom,
    userAtom,
} from "../../stores/jotai/base";
import { discoverySessionAtom } from "../../stores/jotai/discovery-session";
import { useCreateComment, useGetComments } from "../../tanstackQuery/queries/comments";
import IwButton from "../app/IwButton";
import IwIconButton from "../app/IwIconButton";

interface IPresentationLinkTableRowProps {
    periodIndex: number;
    periodAtom: WritableAtom<IPeriod, [SetStateAction<IPeriod>], void>;
}

const PresentationLinkTableRow: FC<IPresentationLinkTableRowProps> = (
    props
) => {
    const { periodIndex, periodAtom } = props;
    const [linkContent, setLinkContent] = useState<string>(undefined);
    const [isSaving, setIsSaving] = useState<boolean>(false);
    const period = useAtomValue(periodAtom);
    const cohort = useAtomValue(cohortAtom);
    const team = useAtomValue(teamAtom);
    const user = useAtomValue(userAtom);
    const { keycloak } = useKeycloak();
    const [discoverySession, setDiscoverySession] = useAtom(discoverySessionAtom);
    const [base, setBase] = useAtom(baseAtom);
    
    const { data: comments } = useGetComments(
        { accessToken: keycloak?.token },
        { groupID: base?.team?.groupID, sessionID: discoverySession?.sessionID },
        { enabled: !!(base?.team?.groupID && discoverySession?.sessionID) }
    );
    
    const link = comments?.find(
        (comment) =>
            comment.slug === "presentation-link" &&
            comment.period === period?.groupPeriodID
    );
    const hasAnyChanges = link?.content !== linkContent;
    const linkUrl = link?.content
        ? `https://${link.content.replace("https://", "")}`
        : "";


    const client = useQueryClient();
    const { mutateAsync: createNewComment } = useCreateComment(
      { accessToken: keycloak?.token },
      {
        onSuccess: () => {
          client.invalidateQueries({ queryKey: ["comments"] });
        }
      }
    );

    const onSave = () => {
        if (hasAnyChanges) {
            setIsSaving(true);

            const token = keycloak?.token;
            //@ts-ignore
            const sid = window?.__sid;

            createNewComment({
              commentID: link?.commentID,
              iterator: periodIndex,
              content: linkContent,
              slug: "presentation-link",
              rating: "",
              period: period?.groupPeriodID,
              cohortID: cohort?.id,
              groupID: team?.groupID,
              userID: user?.userID
            })
              .then((res) => {})
              .catch((error) => {
                console.error(error);
              })
              .finally(() => {
                setIsSaving(false);
              });
        }
    };
    useEffect(() => {
        setLinkContent(link?.content);
    }, [link?.content]);

    return (
        <TableRow>
            <TableCell sx={{ whiteSpace: "nowrap" }}>
                {cohort?.periodLabel} {periodIndex} (
                {moment(period?.endDate).format("MM/DD/YY")})
            </TableCell>
            <TableCell>
                <IwIconButton
                    // @ts-ignore
                    href={linkUrl}
                    target="_blank"
                    disabled={!linkUrl}
                >
                    <OpenInNewRoundedIcon />
                </IwIconButton>
            </TableCell>
            <TableCell sx={{ width: "100%" }}>
                <Stack
                    direction="row"
                    gap={2}
                    sx={{ paddingY: "12px" }}
                >
                    <TextField
                        value={linkContent}
                        size="small"
                        onChange={(event) => {
                            setLinkContent(
                                event.target.value?.trim() || undefined
                            );
                        }}
                        onKeyDown={(event) => {
                            if (event.key === "Enter") {
                                onSave();
                            }
                        }}
                    />
                    <IwButton
                        variant="contained"
                        endIcon={<SaveRoundedIcon />}
                        loading={isSaving}
                        disabled={isSaving || !hasAnyChanges}
                        onClick={onSave}
                    >
                        Save
                    </IwButton>
                </Stack>
            </TableCell>
        </TableRow>
    );
};

export default PresentationLinkTableRow;
